import React, { useState, useEffect } from 'react';
import { Box, Paper, Typography, TextField, Button, Alert, CircularProgress } from '@mui/material';
import { employeeDetailService } from '../services/api';
import { useAuth } from '../contexts/AuthContext';

const MyEmployeeDetails: React.FC = () => {
  const { user } = useAuth();
  const [formData, setFormData] = useState<any>({
    phoneNumber: '', address: '', dateOfBirth: '', panNumber: '', aadharNumber: '', bankAccountNumber: '', bankName: '', bankIFSC: ''
  });
  const [files, setFiles] = useState<any>({ panCard: null, aadharCard: null, resume: null });
  const [message, setMessage] = useState('');
  const [existingData, setExistingData] = useState<any>(null);
  // Starts true so the form/upload buttons (whose "not uploaded yet" state is the default
  // before existingData loads) never flash before the real data arrives.
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user?.id) {
      loadMyDetails();
    }
  }, [user]);

  const loadMyDetails = async () => {
    setLoading(true);
    try {
      const data = await employeeDetailService.getEmployeeDetail(user!.id);
      if (data.id) {
        setExistingData(data);
        setFormData({
          phoneNumber: data.phoneNumber || '',
          address: data.address || '',
          dateOfBirth: data.dateOfBirth ? data.dateOfBirth.split('T')[0] : '',
          panNumber: data.panNumber || '',
          aadharNumber: data.aadharNumber || '',
          bankAccountNumber: data.bankAccountNumber || '',
          bankName: data.bankName || '',
          bankIFSC: data.bankIFSC || ''
        });
      }
    } catch (error) {
      console.error('Error loading details:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const formDataToSend = new FormData();
    formDataToSend.append('userId', user!.id.toString());
    Object.keys(formData).forEach(key => {
      if (formData[key]) formDataToSend.append(key, formData[key]);
    });

    if (files.panCard) formDataToSend.append('panCardFile', files.panCard);
    if (files.aadharCard) formDataToSend.append('aadharCardFile', files.aadharCard);
    if (files.resume) formDataToSend.append('resumeFile', files.resume);

    try {
      const response = await employeeDetailService.saveEmployeeDetail(formDataToSend);
      setMessage(response.message);
      loadMyDetails();
      setFiles({ panCard: null, aadharCard: null, resume: null });
      setTimeout(() => setMessage(''), 3000);
    } catch (error: any) {
      setMessage(error.response?.data?.message || 'Error saving details');
    }
  };

  const hasPan = existingData?.panCardUrl;
  const hasAadhar = existingData?.aadharCardUrl;
  const isPanRejected = existingData?.panVerificationStatus === 'Rejected';
  const isAadharRejected = existingData?.aadharVerificationStatus === 'Rejected';

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h6" gutterBottom>My Details</Typography>

      <Paper sx={{ p: 3, maxWidth: 800 }}>
        {message && <Alert severity="info" sx={{ mb: 2 }}>{message}</Alert>}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress />
          </Box>
        ) : (
        <>
        <form onSubmit={handleSubmit}>
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 2 }}>
            <TextField fullWidth label="Phone Number" value={formData.phoneNumber} onChange={(e) => setFormData({ ...formData, phoneNumber: e.target.value })} size="small" required />
            <TextField fullWidth label="Date of Birth" type="date" value={formData.dateOfBirth} onChange={(e) => setFormData({ ...formData, dateOfBirth: e.target.value })} size="small" InputLabelProps={{ shrink: true }} required />
            <Box sx={{ gridColumn: '1 / -1' }}><TextField fullWidth label="Address" multiline rows={2} value={formData.address} onChange={(e) => setFormData({ ...formData, address: e.target.value })} size="small" required /></Box>
            <TextField fullWidth label="PAN Number" value={formData.panNumber || ''} onChange={(e) => setFormData({ ...formData, panNumber: e.target.value })} size="small" required />
            <TextField fullWidth label="Aadhar Number" value={formData.aadharNumber || ''} onChange={(e) => setFormData({ ...formData, aadharNumber: e.target.value })} size="small" required />
            <Box sx={{ gridColumn: '1 / -1' }}><TextField fullWidth label="Bank Account Number" value={formData.bankAccountNumber} onChange={(e) => setFormData({ ...formData, bankAccountNumber: e.target.value })} size="small" required /></Box>
            <TextField fullWidth label="Bank Name" value={formData.bankName} onChange={(e) => setFormData({ ...formData, bankName: e.target.value })} size="small" />
            <TextField fullWidth label="Bank IFSC" value={formData.bankIFSC} onChange={(e) => setFormData({ ...formData, bankIFSC: e.target.value })} size="small" />
            
            <Box sx={{ gridColumn: '1 / -1', mt: 2 }}>
              <Typography variant="body2" sx={{ mb: 1 }}>Upload Documents</Typography>
              
              {!hasPan || isPanRejected ? (
                <>
                  {isPanRejected && (
                    <Alert severity="error" sx={{ mb: 1 }}>
                      PAN Card: Rejected
                      <Typography variant="caption" display="block">Reason: {existingData.panRejectionReason}</Typography>
                      <Button size="small" onClick={() => window.open(hasPan, '_blank')}>View Rejected</Button>
                    </Alert>
                  )}
                  <Button variant="outlined" component="label" fullWidth sx={{ mb: 1 }} color={!files.panCard ? 'error' : 'primary'}>
                    {isPanRejected ? 'Re-upload PAN Card *' : 'Upload PAN Card (Required) *'}
                    <input type="file" hidden accept=".pdf,.jpg,.jpeg,.png" onChange={(e) => setFiles({ ...files, panCard: e.target.files?.[0] })} required />
                  </Button>
                  {files.panCard && <Typography variant="caption" color="success.main">{files.panCard.name}</Typography>}
                </>
              ) : (
                <Alert severity={existingData?.panVerificationStatus === 'Approved' ? 'success' : 'warning'} sx={{ mb: 1 }}>
                  PAN Card: {existingData?.panVerificationStatus || 'Pending'}
                  <Button size="small" onClick={() => window.open(hasPan, '_blank')}>View</Button>
                </Alert>
              )}

              {!hasAadhar || isAadharRejected ? (
                <>
                  {isAadharRejected && (
                    <Alert severity="error" sx={{ mb: 1, mt: 1 }}>
                      Aadhar Card: Rejected
                      <Typography variant="caption" display="block">Reason: {existingData.aadharRejectionReason}</Typography>
                      <Button size="small" onClick={() => window.open(hasAadhar, '_blank')}>View Rejected</Button>
                    </Alert>
                  )}
                  <Button variant="outlined" component="label" fullWidth sx={{ mb: 1, mt: 1 }} color={!files.aadharCard ? 'error' : 'primary'}>
                    {isAadharRejected ? 'Re-upload Aadhar Card *' : 'Upload Aadhar Card (Required) *'}
                    <input type="file" hidden accept=".pdf,.jpg,.jpeg,.png" onChange={(e) => setFiles({ ...files, aadharCard: e.target.files?.[0] })} required />
                  </Button>
                  {files.aadharCard && <Typography variant="caption" color="success.main">{files.aadharCard.name}</Typography>}
                </>
              ) : (
                <Alert severity={existingData?.aadharVerificationStatus === 'Approved' ? 'success' : 'warning'} sx={{ mb: 1 }}>
                  Aadhar Card: {existingData?.aadharVerificationStatus || 'Pending'}
                  <Button size="small" onClick={() => window.open(hasAadhar, '_blank')}>View</Button>
                </Alert>
              )}

              <Button variant="outlined" component="label" fullWidth sx={{ mb: 1, mt: 1 }}>
                {existingData?.resumeUrl ? 'Update Resume' : 'Upload Resume'}
                <input type="file" hidden accept=".pdf,.doc,.docx" onChange={(e) => setFiles({ ...files, resume: e.target.files?.[0] })} />
              </Button>
              {files.resume && <Typography variant="caption">{files.resume.name}</Typography>}
              {existingData?.resumeUrl && !files.resume && <Button size="small" onClick={() => window.open(existingData.resumeUrl, '_blank')} sx={{ ml: 1 }}>View Current</Button>}
            </Box>
          </Box>

          <Button type="submit" variant="contained" fullWidth sx={{ mt: 3 }}>Save Details</Button>
        </form>

        {existingData && (
          <Box sx={{ mt: 3, p: 2, bgcolor: '#f8fafc', borderRadius: 1 }}>
            <Typography variant="subtitle2" gutterBottom>Read-Only Information (Contact admin to update)</Typography>
            <Typography variant="body2">Department: {existingData.department || '-'}</Typography>
            <Typography variant="body2">Designation: {existingData.designation || '-'}</Typography>
            <Typography variant="body2">Joining Date: {existingData.joiningDate ? new Date(existingData.joiningDate).toLocaleDateString() : '-'}</Typography>
          </Box>
        )}
        </>
        )}
      </Paper>
    </Box>
  );
};

export default MyEmployeeDetails;
